import { AlertBanner } from '@/components/common/AlertBanner'
import { Navbar } from '@/components/Navbar'
import { OrderFormViewModal } from '@/components/modals/OrderFormViewModal'
import { OrderDetailsPanel } from '@/components/order/OrderDetailsPanel'
import { CriticalResultBanner } from '@/components/order/CriticalResultBanner'
import { formatReportTimestamp } from '@/components/order/reportTimestamp'
import { OrderExternalIdLabel } from '@/components/order/OrderExternalIdLabel'
import { OrderReportDocumentsMenu } from '@/components/order/OrderReportDocumentsMenu'
import { OrderResultsPdfPanel } from '@/components/order/OrderResultsPdfPanel'
import { getClientName, getFulfillmentStrategy } from '@/config/uiConfig'
import { useFeatureFlag, useUiConfig } from '@/config/uiConfigHooks'
import { useConfiguredOrderSteps } from '@/hooks/useConfiguredOrderSteps'
import {
  getAllOrderReports,
  getCurrentPractitioner,
  getReportCriticality,
  getReportReviewed,
  markReportReviewed,
} from '@/features/report/services/reportClient'
import { guidanceStatusOf, selectFinalGuidanceReport, selectLatestReport } from '@/features/report/selectFinalGuidanceReport'
import { legacyTrfPayloadToCanonical } from '@/ordering/canonicalOrder'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setCanonicalOrder } from '@/store/slices/canonicalOrderSlice'
import type { OrderReportResponse } from '@/types/OrderReportTypes'
import type { OrderFormInfoData } from '@/types/RequisitionFormTypes'
import { getOrderById, getOrderByPatient, getOrderStatusByPatient, inflectionExternalIdFromOrder, OrderStatus } from '@/utils/DatabaseOperations'
import { formatAge, formatBirthDate, formatMrnForDisplay } from '@/utils/orderFormUtils'
import { useAuth0 } from '@auth0/auth0-react'
import * as Sentry from '@sentry/react'
import { ArrowsRepeat, InfoCircle, Minus } from 'flowbite-react-icons/outline'
import { OrderStepsRow } from '@/components/order/OrderStepsRow'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'

type OrderResultReadyState =
  | {
      /** Epic flow state (from Callback); use for auth flow detection */
      epicState?: string
      orderId?: string
      orderData?: OrderFormInfoData
      backendStatus?: string
    }
  | string
  | null
  | undefined

const REPORT_STATUS_LABELS: Record<string, string> = {
  final: 'Final report generated',
  prelim: 'Preliminary report generated',
  corrected: 'Corrected report generated',
  re_analysis_full: 'Full re-analysis report generated',
  amended: 'Amended report generated',
}

function formatReportStatus(report: { status?: string | null }): string {
  const status = report.status?.toLowerCase()
  if (status && REPORT_STATUS_LABELS[status]) return REPORT_STATUS_LABELS[status]
  return 'Updated'
}

export function OrderResultReady() {
  const { config } = useUiConfig()
  const guidanceUiEnabled = useFeatureFlag('guidance')
  const navigate = useNavigate()
  const location = useLocation()
  const params = useParams<{ orderId?: string }>()
  const { user: auth0User, logout: auth0Logout } = useAuth0()

  const dispatch = useAppDispatch()
  const locationState = location.state as OrderResultReadyState
  const orderDataFromState = typeof locationState === 'object' && locationState !== null ? locationState.orderData : undefined
  const initialOrderIdFromState =
    typeof locationState === 'object' && locationState !== null ? locationState.orderId : undefined
  const [fetchedOrderData, setFetchedOrderData] = useState<OrderFormInfoData | undefined>(undefined)
  const orderData = orderDataFromState ?? fetchedOrderData

  const epicState =
    typeof locationState === 'object' && locationState !== null
      ? (locationState.epicState ?? (locationState as { state?: string }).state)
      : null
  const authFlow = epicState ? 'epic' : 'standalone'
  const handleLogout = () => {
    auth0Logout({ logoutParams: { returnTo: window.location.origin + '/login' } })
  }

  const [resolvedOrderId, setResolvedOrderId] = useState<string | undefined>(params.orderId || initialOrderIdFromState)
  const [inflectionExternalId, setInflectionExternalId] = useState<string | null>(null)
  // Raw backend order status (e.g. "reanalysis"), captured from the status
  // check below. Used to hide "Request Reanalysis" while a reanalysis is
  // already in progress.
  const [backendStatus, setBackendStatus] = useState<string | null>(null)
  const [isOrderFormModalOpen, setIsOrderFormModalOpen] = useState(false)
  const [criticalReportId, setCriticalReportId] = useState<string | null>(null)
  const [criticalReportVersions, setCriticalReportVersions] = useState<OrderReportResponse[]>([])
  const [reviewedStatusMap, setReviewedStatusMap] = useState<Record<string, boolean>>({})
  const [criticalityMap, setCriticalityMap] = useState<Record<string, boolean>>({})
  const [isCriticalAndUnreviewed, setIsCriticalAndUnreviewed] = useState(false)
  const [isConfirmingReview, setIsConfirmingReview] = useState(false)
  const [criticalAcknowledged, setCriticalAcknowledged] = useState(false)
  const [displayedReportId, setDisplayedReportId] = useState<string | null>(null)
  const [currentPractitionerId, setCurrentPractitionerId] = useState<string | null>(null)
  const [mainPanelView, setMainPanelView] = useState<'results' | 'orderDetails'>('results')
  const [orderReportsList, setOrderReportsList] = useState<OrderReportResponse[]>([])
  const [orderReportsLoading, setOrderReportsLoading] = useState(false)
  const [orderReportsError, setOrderReportsError] = useState<string | null>(null)
  const orderReportsRequestRef = useRef<string | null>(null)

  // Guidance notice shown on the results page (guidance feature on, results
  // loaded, and no finalized guidance — a final envelope routes to the Guidance
  // page instead). Driven by the latest report's guidance status so it survives
  // refresh and direct visits:
  //   - 'excluded': guidance was rejected → Next Steps intentionally omitted.
  //   - 'pending':  any other non-final status → guidance not yet available.
  const guidanceNotice: 'none' | 'excluded' | 'pending' = useMemo(() => {
    if (!guidanceUiEnabled || orderReportsLoading || orderReportsError) return 'none'
    if (orderReportsList.length === 0) return 'none'
    if (selectFinalGuidanceReport(orderReportsList)) return 'none'
    const latest = selectLatestReport(orderReportsList)
    return latest && guidanceStatusOf(latest) === 'rejected' ? 'excluded' : 'pending'
  }, [guidanceUiEnabled, orderReportsList, orderReportsLoading, orderReportsError])

  // Safety net: the canonical home for finalized guidance is the Guidance page.
  // Any entry point that lands here with a final guidance envelope (Epic
  // callback, direct link, bookmark, browser back/forward) self-corrects by
  // redirecting once reports have loaded. `replace` avoids trapping the user in
  // a back-button bounce between Results and Guidance.
  useEffect(() => {
    if (!guidanceUiEnabled || orderReportsLoading || orderReportsError) return
    if (!resolvedOrderId) return
    if (!selectFinalGuidanceReport(orderReportsList)) return
    navigate(`/order/guidance/${encodeURIComponent(resolvedOrderId)}`, {
      replace: true,
      state: locationState,
    })
  }, [
    guidanceUiEnabled,
    orderReportsLoading,
    orderReportsError,
    orderReportsList,
    resolvedOrderId,
    navigate,
    locationState,
  ])

  const { canonicalOrder: storedCanonical } = useAppSelector((s) => s.canonicalOrder)

  const birthDate = useMemo(() => {
    if (orderData?.patient?.birthDate) {
      return orderData.patient.birthDate
    }
    if (storedCanonical?.patient?.dateOfBirth) {
      return storedCanonical.patient.dateOfBirth
    }
    return null
  }, [storedCanonical?.patient?.dateOfBirth, orderData?.patient?.birthDate])

  const patientName =
    orderData?.patient?.name?.firstName || orderData?.patient?.name?.lastName
      ? [orderData?.patient?.name?.firstName, orderData?.patient?.name?.middleName, orderData?.patient?.name?.lastName]
          .filter(Boolean)
          .join(' ')
      : `${storedCanonical?.patient?.firstName ?? ''} ${storedCanonical?.patient?.lastName ?? ''}`.trim()

  const mrn = orderData?.patient?.mrn || storedCanonical?.patient?.mrn || ''
  const mrnDisplay = formatMrnForDisplay(mrn)
  const ageDisplay = formatAge(birthDate)
  const dobDisplay = formatBirthDate(birthDate)

  const steps = useConfiguredOrderSteps({ currentStepId: 'results' })

  // Fetch the current user's linked practitioner_id from /auth/practitioner
  useEffect(() => {
    let cancelled = false
    getCurrentPractitioner()
      .then((result) => {
        if (!cancelled && result.practitionerId) {
          setCurrentPractitionerId(result.practitionerId)
        }
      })
      .catch((err) => {
        Sentry.captureException(err, { tags: { operation: 'auth.practitioner' } })
      })
    return () => { cancelled = true }
  }, [])

  // Sync resolvedOrderId with URL param when it changes (e.g. browser back/forward or direct navigation)
  useEffect(() => {
    if (params.orderId) {
      setResolvedOrderId(params.orderId)
    }
  }, [params.orderId])

  // Load full order (orderFormInfo + canonical) for the results header (name, MRN, DOB) and shared UI.
  // Order details / PDF / reports still need the same payload; deferring fetch until "Order Details"
  // left the Test Results tab with no patient row in the header.
  useEffect(() => {
    if (orderDataFromState || fetchedOrderData || !resolvedOrderId) return

    let cancelled = false
    const fetchOrder = async () => {
      try {
        const caller = auth0User?.email || auth0User?.sub || 'standalone-user'
        const order = await getOrderById(resolvedOrderId, caller)
        if (cancelled || !order) return

        const resolvedExternalId = inflectionExternalIdFromOrder(order)
        if (resolvedExternalId) {
          setInflectionExternalId(resolvedExternalId)
        }

        if (!order.json_payload) return

        if (order.json_payload.orderFormInfo) {
          setFetchedOrderData(order.json_payload.orderFormInfo)
        }
        if (order.json_payload.requisitionFormData) {
          dispatch(setCanonicalOrder(legacyTrfPayloadToCanonical({
            orderFormInfo: order.json_payload.orderFormInfo,
            requisitionFormData: order.json_payload.requisitionFormData,
          })))
        }
      } catch (err) {
        Sentry.captureException(err, { tags: { operation: 'order.results.fetch-order' } })
      }
    }
    fetchOrder()
    return () => { cancelled = true }
  }, [
    resolvedOrderId,
    orderDataFromState,
    fetchedOrderData,
    auth0User?.email,
    auth0User?.sub,
    dispatch,
  ])

  const reportCaller = useMemo(() => {
    return auth0User?.email || auth0User?.sub || 'unknown-user'
  }, [auth0User?.email, auth0User?.sub])

  // Single report list for this page: documents menu, results card, and criticality checks (no triple getAllOrderReports).
  useEffect(() => {
    if (!resolvedOrderId) {
      setOrderReportsList([])
      setOrderReportsError(null)
      setOrderReportsLoading(false)
      orderReportsRequestRef.current = null
      return
    }

    const requestKey = `${resolvedOrderId}::${reportCaller}`
    orderReportsRequestRef.current = requestKey
    let cancelled = false

    const run = async () => {
      setOrderReportsLoading(true)
      setOrderReportsError(null)
      try {
        const reports = await getAllOrderReports(resolvedOrderId, reportCaller)
        if (cancelled || orderReportsRequestRef.current !== requestKey) return
        setOrderReportsList(reports)
      } catch (err) {
        if (cancelled || orderReportsRequestRef.current !== requestKey) return
        Sentry.captureException(err, { tags: { operation: 'order.results.fetch-reports-list' } })
        setOrderReportsError(err instanceof Error ? err.message : 'Failed to fetch reports.')
        setOrderReportsList([])
      } finally {
        if (!cancelled && orderReportsRequestRef.current === requestKey) {
          setOrderReportsLoading(false)
        }
      }
    }
    void run()
    return () => {
      cancelled = true
    }
  }, [resolvedOrderId, reportCaller])

  // Fetch criticality only for the single most recent report. Versioned
  // amendments (prelim → final → amended → corrected → re-analysis) inherit
  // the pipeline's criticality flag per row, so a patient with N historical
  // versions of the same finding would otherwise trigger N ack-required
  // banners. Clinically the latest published version supersedes all earlier
  // ones, so we gate the "Critical Result Received" banner on: the newest
  // report is itself critical AND not yet reviewed.
  useEffect(() => {
    if (!resolvedOrderId || criticalAcknowledged) return
    if (orderReportsLoading || orderReportsError) return
    const reports = orderReportsList
    if (!reports?.length) return

    let cancelled = false
    const run = async () => {
      try {
        const latest = [...reports].sort((a, b) => {
          if (a.version != null && b.version != null) return b.version - a.version
          return (b.created_at ?? '').localeCompare(a.created_at ?? '')
        })[0]
        if (!latest) return

        let isCritical = false
        let isReviewed = false
        try {
          const [crit, rev] = await Promise.all([
            getReportCriticality(resolvedOrderId, latest.id, reportCaller),
            getReportReviewed(resolvedOrderId, latest.id, reportCaller),
          ])
          isCritical = crit.criticality?.toLowerCase() === 'critical'
          isReviewed = rev.reviewed
        } catch {
          return
        }
        if (cancelled) return

        if (isCritical && !isReviewed) {
          setCriticalReportId(latest.id)
          setCriticalReportVersions([latest])
          setReviewedStatusMap({ [latest.id]: false })
          setCriticalityMap({ [latest.id]: true })
          setIsCriticalAndUnreviewed(true)
        }
      } catch (err) {
        if (!cancelled) {
          Sentry.captureException(err, { tags: { operation: 'order.report-criticality' } })
        }
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [
    resolvedOrderId,
    criticalAcknowledged,
    reportCaller,
    orderReportsList,
    orderReportsLoading,
    orderReportsError,
  ])

  // Best-effort: ensure we have an orderId to fetch reports
  useEffect(() => {
    if (resolvedOrderId) {
      return
    }
    if (!orderData?.patient?.id) {
      return
    }

    let isActive = true
    const resolveId = async () => {
      try {
        const caller =
          orderData?.orderingProvider?.name?.firstName || orderData?.orderingProvider?.name?.lastName
            ? `${orderData.orderingProvider?.name?.firstName || ''} ${orderData.orderingProvider?.name?.lastName || ''}`
            : 'spa'
        const existingOrder = await getOrderByPatient(orderData.patient.id, caller.trim() || 'spa')
        if (!isActive) {
          return
        }
        if (existingOrder?.id) {
          setResolvedOrderId(existingOrder.id)
          navigate(`/order/results/${encodeURIComponent(existingOrder.id)}`, { replace: true, state: locationState })
        }
      } catch (error) {
        Sentry.captureException(error, { tags: { operation: 'order.results.id.resolve' } })
      }
    }
    resolveId()

    return () => {
      isActive = false
    }
  }, [
    locationState,
    navigate,
    orderData?.orderingProvider?.name?.firstName,
    orderData?.orderingProvider?.name?.lastName,
    orderData?.patient?.id,
    resolvedOrderId,
  ])

  const handleConfirmCriticalReview = async () => {
    const targetId = displayedReportId ?? criticalReportId
    if (!resolvedOrderId || !targetId) return
    setIsConfirmingReview(true)
    try {
      await markReportReviewed(resolvedOrderId, targetId, reportCaller, currentPractitionerId)
      const updatedMap = { ...reviewedStatusMap, [targetId]: true }
      setReviewedStatusMap(updatedMap)
      const allCriticalReviewed = criticalReportVersions
        .filter((r) => criticalityMap[r.id])
        .every((r) => updatedMap[r.id])
      if (allCriticalReviewed) {
        setCriticalAcknowledged(true)
        setIsCriticalAndUnreviewed(false)
      }
    } catch (err) {
      Sentry.captureException(err, { tags: { operation: 'order.report.mark-reviewed' } })
    } finally {
      setIsConfirmingReview(false)
    }
  }

  // If we have enough context to check status and it isn't Result Ready, bounce back.
  useEffect(() => {
    let isActive = true

    const ensureResultReady = async () => {
      if (!orderData?.patient?.id) {
        return
      }
      const caller =
        orderData?.orderingProvider?.name?.firstName || orderData?.orderingProvider?.name?.lastName
          ? `${orderData.orderingProvider?.name?.firstName || ''} ${orderData.orderingProvider?.name?.lastName || ''}`
          : 'spa'
      try {
        const statusResponse = await getOrderStatusByPatient(orderData.patient.id, caller.trim() || 'spa')
        if (!isActive) {
          return
        }
        if (!statusResponse) {
          return
        }
        const resolvedExternalId = inflectionExternalIdFromOrder(statusResponse)
        if (resolvedExternalId) {
          setInflectionExternalId(resolvedExternalId)
        }
        setBackendStatus(statusResponse.status ?? null)
        // A ``reanalysis`` order is a completed order with an in-flight
        // reanalysis; its results still exist and are viewable ("View Initial
        // Report"), so treat it like COMPLETE here rather than bouncing back to
        // the in-progress page.
        if (
          statusResponse.status !== OrderStatus.COMPLETE &&
          statusResponse.status !== OrderStatus.REANALYSIS
        ) {
          navigate('/order/in-progress', { replace: true, state: locationState })
        }
      } catch (error) {
        Sentry.captureException(error, { tags: { operation: 'order.results.status.check' } })
      }
    }
    ensureResultReady()

    return () => {
      isActive = false
    }
  }, [
    locationState,
    navigate,
    orderData?.orderingProvider?.name?.firstName,
    orderData?.orderingProvider?.name?.lastName,
    orderData?.patient?.id,
  ])

  // "Request Reanalysis" is only offered for inflection_test fulfillment, only
  // once at least one report exists for the order, and NOT while a reanalysis is
  // already in progress (backend status "reanalysis") — you can't request a
  // second reanalysis until the current one resolves back to "complete".
  const showActionsMenu =
    getFulfillmentStrategy(config) === 'inflection_test' &&
    orderReportsList.length > 0 &&
    backendStatus !== OrderStatus.REANALYSIS

  const handleRequestReanalysis = () => {
    if (!resolvedOrderId) return
    const patientId = orderData?.patient?.id ?? ''
    // `reanalysis=true` signals the in-progress page that this edit session was
    // started as a reanalysis request (so it can branch its Send Updates copy /
    // payload). The value is carried in the URL so it survives reloads and is
    // visible/inspectable, in addition to the routing state.
    navigate(
      `/order/in-progress/${encodeURIComponent(resolvedOrderId)}/${encodeURIComponent(patientId)}?reanalysis=true`,
      { state: locationState },
    )
  }

  const orderDetailsSection = <OrderDetailsPanel />

  return (
    <div className={authFlow === 'standalone' ? 'page-container-standalone' : 'page-container'}>
      {authFlow === 'standalone' && (
        <Navbar
          userAvatar={auth0User?.picture}
          userName={auth0User?.name}
          onLogout={handleLogout}
          showNavLinks={true}
          organizationName={getClientName(config)}
        />
      )}
      <div className={`flex w-full flex-col gap-4 ${authFlow === 'standalone' ? 'px-6' : ''}`}>
        <div
          className={`sticky top-0 z-20 border-border-base bg-white p-6 shadow-sm ${
            authFlow === 'standalone' ? '-mx-6 border-b' : 'rounded-base border'
          }`}
        >
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-6">
              <div className="flex flex-col gap-1">
                <h1 className="text-2xl font-semibold tracking-tight text-text-heading">{patientName || 'Patient'}</h1>
                <span className="text-xs font-medium text-text-body">{mrnDisplay || 'MRN not available'}</span>
                {inflectionExternalId ? <OrderExternalIdLabel externalId={inflectionExternalId} /> : null}
              </div>
              <div className="hidden h-10 w-px bg-border-base sm:block" />
              <div className="flex flex-col gap-1">
                <span className="text-sm font-bold text-text-heading">{ageDisplay || 'Age unavailable'}</span>
                <span className="text-xs font-medium text-text-body">{dobDisplay || 'DOB unavailable'}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {guidanceUiEnabled ? (
                <OrderReportDocumentsMenu
                  orderId={resolvedOrderId}
                  orderData={orderData}
                  onOpenOrderForm={() => setIsOrderFormModalOpen(true)}
                  useParentReportList={true}
                  parentReports={orderReportsList}
                  parentReportsLoading={orderReportsLoading}
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setIsOrderFormModalOpen(true)}
                  className="inline-flex items-center gap-2 rounded-full border border-border-base bg-white px-4 py-2 text-sm font-medium text-text-body shadow-xs transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  View Order Form
                </button>
              )}
              {guidanceUiEnabled ? (
                <button
                  type="button"
                  onClick={() => setMainPanelView((v) => (v === 'results' ? 'orderDetails' : 'results'))}
                  className="inline-flex items-center gap-2 rounded-full border border-border-base bg-white px-4 py-2 text-sm font-medium text-text-body shadow-xs transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  {mainPanelView === 'results' ? 'Order Details' : 'Show Results'}
                </button>
              ) : null}
              {showActionsMenu ? (
                <button
                  type="button"
                  onClick={handleRequestReanalysis}
                  className="inline-flex shrink-0 items-center gap-2 rounded-full border border-border-base bg-white px-4 py-2 text-sm font-medium text-text-body shadow-xs transition-colors hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                >
                  <ArrowsRepeat className="h-5 w-5 shrink-0" aria-hidden />
                  <span>Reanalysis/Comparators</span>
                </button>
              ) : null}
            </div>
          </div>
        </div>

        <div className="rounded-base border border-border-base bg-white px-6 pb-6 pt-12 shadow-sm">
          {/* Guidance intentionally omitted (rejected → no Next Steps): override the
              guidance step's icon with a dash to signal "no guidance available". */}
          <OrderStepsRow
            variant="tracker"
            labelTone="result"
            steps={steps.map((step) =>
              step.id === 'guidance' && guidanceNotice === 'excluded'
                ? { ...step, iconOverride: Minus }
                : step
            )}
          />

          <div className="mt-6 flex flex-col gap-4">
            {isCriticalAndUnreviewed && (
              <CriticalResultBanner
                reports={criticalReportVersions}
                onAcknowledge={handleConfirmCriticalReview}
                isConfirming={isConfirmingReview}
              />
            )}
            {criticalAcknowledged && criticalReportVersions.length > 0 && (
              <div
                className="flex min-h-[100px] w-full gap-4 rounded-base border border-[var(--colors-border-border-base-medium,#E5E7EB)] bg-[var(--colors-background-bg-secondary-medium,#FAFAFA)] p-4"
              >
                <InfoCircle className="h-5 w-5 shrink-0 text-text-heading" />
                <div className="flex flex-1 flex-col gap-1">
                  <p className="text-sm font-semibold leading-5 text-text-heading">
                    Critical Result
                  </p>
                  <div className="font-sans text-sm font-medium leading-5 text-text-heading">
                    {criticalReportVersions.map((report) => {
                      const ts = formatReportTimestamp(report.generated_at) ?? formatReportTimestamp(report.created_at)
                      const statusLabel = formatReportStatus(report)
                      return (
                        <p key={report.id}>
                          {statusLabel}{ts ? ` on ${ts}` : ''}.
                        </p>
                      )
                    })}
                  </div>
                </div>
              </div>
            )}
            {guidanceNotice === 'excluded' && (
              <div className="flex w-full gap-4 rounded-base border border-[var(--colors-border-border-warning-subtle,#FCD9BD)] bg-[var(--colors-background-bg-warning-soft,#FFF8F1)] p-4">
                <InfoCircle className="h-5 w-5 shrink-0 text-[var(--colors-text-text-fg-warning,#771D1D)]" />
                <p className="flex-1 text-sm font-medium leading-5 text-[var(--colors-text-text-fg-warning,#771D1D)]">
                  This report excludes Next Steps guidance as it does not reflect a single-condition
                  diagnostic finding.
                </p>
              </div>
            )}
            {guidanceNotice === 'pending' && (
              <AlertBanner useInflectionIcon={true}>
                <span className="font-semibold">Guidance is not yet available.</span> Guidance is
                currently provided for diagnostic findings in a single condition.
              </AlertBanner>
            )}
          </div>
        </div>

        <div className="rounded-base border border-border-base bg-white p-6 shadow-sm">
          {!guidanceUiEnabled ? (
            <div className="mb-4 flex gap-2" role="tablist" aria-label="Order results">
              <button
                type="button"
                role="tab"
                aria-selected={mainPanelView === 'results'}
                id="order-results-tab-results"
                aria-controls="order-results-tab-panel"
                onClick={() => setMainPanelView('results')}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600/20 ${
                  mainPanelView === 'results'
                    ? 'bg-brand-600 text-white shadow-xs dark:bg-brand-600 dark:text-white'
                    : 'border border-border-base bg-white text-text-body shadow-xs hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                }`}
              >
                Test Results
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={mainPanelView === 'orderDetails'}
                id="order-results-tab-order-details"
                aria-controls="order-results-tab-panel"
                onClick={() => setMainPanelView('orderDetails')}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-brand-600/20 ${
                  mainPanelView === 'orderDetails'
                    ? 'bg-brand-600 text-white shadow-xs dark:bg-brand-600 dark:text-white'
                    : 'border border-border-base bg-white text-text-body shadow-xs hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                }`}
              >
                Order Details
              </button>
            </div>
          ) : null}
          <div
            id={guidanceUiEnabled ? undefined : 'order-results-tab-panel'}
            role={guidanceUiEnabled ? undefined : 'tabpanel'}
            aria-labelledby={
              guidanceUiEnabled
                ? undefined
                : mainPanelView === 'results'
                  ? 'order-results-tab-results'
                  : 'order-results-tab-order-details'
            }
            className={`flex flex-col ${mainPanelView === 'orderDetails' ? 'gap-4' : ''}`}
          >
            {mainPanelView === 'results' ? (
              <OrderResultsPdfPanel
                orderId={resolvedOrderId}
                orderData={orderData}
                onReportChange={setDisplayedReportId}
                parentReports={orderReportsList}
                parentReportsLoading={orderReportsLoading}
                parentReportsError={orderReportsError}
              />
            ) : (
              orderDetailsSection
            )}
          </div>
        </div>
      </div>

      <OrderFormViewModal
        isOpen={isOrderFormModalOpen}
        onClose={() => setIsOrderFormModalOpen(false)}
        canonical={storedCanonical}
        formData={null}
        orderData={orderData ?? null}
        orderId={resolvedOrderId}
        caller={auth0User?.email || auth0User?.sub || 'standalone-user'}
      />
    </div>
  )
}
